import React from "react";
import { X, Plus, AlertCircle, CheckCircle2 } from "lucide-react";

const LABELS = ["A", "B", "C", "D", "E", "F"];
export const MAX_OPTIONS = 6;
export const MIN_OPTIONS = 2;
const DEFAULT_OPTIONS = 4;

export const newOption = (text = "") => ({ id: `o_${crypto.randomUUID()}`, text });

export const newQuestion = () => ({
  id: `q_${crypto.randomUUID()}`,
  text: "",
  // Four blanks because almost everything authored here is a 4-option question;
  // spare rows get deleted for the occasional 2- or 3-option one.
  options: Array.from({ length: DEFAULT_OPTIONS }, () => newOption()),
  correct_option_id: null,
});

export const InlineError = ({ msg }) =>
  msg ? (
    <p className="flex items-center gap-1.5 mt-1 text-xs text-red-400">
      <AlertCircle size={11} />
      {msg}
    </p>
  ) : null;

/**
 * Options for one question. The question text itself is edited in the accordion
 * header, so it is never rendered twice.
 */
const QuestionEditor = ({ question, onChange }) => {
  // Clearing the text of the currently-correct option un-marks it too — otherwise a
  // blank input could sit highlighted "correct" until the next save-time check.
  const setOptionText = (optionId, text) =>
    onChange({
      ...question,
      options: question.options.map((o) => (o.id === optionId ? { ...o, text } : o)),
      correct_option_id:
        question.correct_option_id === optionId && !text.trim() ? null : question.correct_option_id,
    });

  // Correct answer is stored as an option ID, so renaming an option's text can
  // never invalidate which answer is correct.
  const setCorrect = (optionId) => onChange({ ...question, correct_option_id: optionId });

  const addOption = () => {
    if (question.options.length >= MAX_OPTIONS) return;
    onChange({ ...question, options: [...question.options, newOption()] });
  };

  const removeOption = (optionId) => {
    if (question.options.length <= MIN_OPTIONS) return;
    onChange({
      ...question,
      options: question.options.filter((o) => o.id !== optionId),
      correct_option_id:
        question.correct_option_id === optionId ? null : question.correct_option_id,
    });
  };

  // One filled option is not a choice yet, so there is nothing meaningful to mark
  // correct until a second one exists.
  const filledCount = question.options.filter((o) => o.text.trim()).length;

  return (
    <div className="space-y-1.5">
      {question.options.map((option, i) => {
        const isSelected = question.correct_option_id === option.id;
        const hasText = !!option.text.trim();
        // An already-marked option stays clickable however few are filled, so emptying
        // another one can never strand a mark the user has no way to undo.
        const canMark = hasText && (isSelected || filledCount >= MIN_OPTIONS);
        return (
          <div
            key={option.id}
            className={`flex items-center gap-2.5 px-3 py-1.5 rounded-xl border transition-all duration-150 ${
              isSelected
                ? "border-emerald-400/50 bg-emerald-500/10"
                : "border-white/10 bg-bgSecondary dark:glass-effect"
            }`}
          >
            <span
              className={`text-sm font-semibold w-4 shrink-0 ${
                isSelected ? "text-emerald-400" : "dark:text-gray-500 text-textSecondary"
              }`}
            >
              {LABELS[i]}
            </span>
            <input
              type="text"
              placeholder={`Option ${LABELS[i]} text...`}
              value={option.text}
              onChange={(e) => setOptionText(option.id, e.target.value)}
              maxLength={200}
              className="flex-1 min-w-0 rounded-lg border dark:text-white text-textPrimary text-sm px-2.5 py-1.5 outline-none transition-colors placeholder:dark:text-gray-600 placeholder:text-gray-400 border-gray-300 dark:border-white/10 focus:border-purple-400 bg-bgPrimary dark:glass-effect"
            />
            {/* A disabled button receives no pointer events, so its own title never
                fires — the reason it's disabled has to live on a wrapper. */}
            <span
              className="inline-flex shrink-0"
              title={
                !hasText
                  ? "Add option text before marking it correct"
                  : !canMark
                  ? "Fill in at least two options before marking the correct answer"
                  : isSelected
                  ? "Click to unmark as the correct answer"
                  : "Mark as the correct answer"
              }
            >
              <button
                type="button"
                disabled={!canMark}
                aria-pressed={isSelected}
                onClick={() => setCorrect(isSelected ? null : option.id)}
                // Fixed min-width so this pill's own label change ("Mark correct" vs the
                // shorter "Correct") never shifts the delete button beside it.
                className={`min-w-[104px] shrink-0 flex items-center justify-center gap-1 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                  !canMark
                    ? "cursor-not-allowed opacity-40 border-gray-300 dark:border-white/20 dark:text-gray-500 text-textSecondary"
                    : isSelected
                    ? "cursor-pointer border-emerald-600 bg-emerald-600 text-white"
                    : "cursor-pointer border-gray-300 dark:border-white/20 dark:text-gray-400 text-textSecondary hover:border-gray-400 dark:hover:border-white/40 hover:text-textPrimary dark:hover:text-white"
                }`}
              >
                {isSelected ? (
                  <>
                    <CheckCircle2 size={13} /> Correct
                  </>
                ) : (
                  "Mark correct"
                )}
              </button>
            </span>
            {question.options.length > MIN_OPTIONS && (
              <button
                type="button"
                onClick={() => removeOption(option.id)}
                title="Remove option"
                className="p-1 rounded-md text-gray-400 dark:text-gray-600 hover:text-red-400 transition-colors shrink-0"
              >
                <X size={13} />
              </button>
            )}
          </div>
        );
      })}

      {question.options.length < MAX_OPTIONS && (
        <div className="pt-0.5">
          <button
            type="button"
            onClick={addOption}
            className="group flex items-center gap-1.5 text-xs dark:text-gray-400 text-textSecondary transition-colors"
          >
            <Plus size={13} className="group-hover:text-blue-400 transition-colors" />
            <span className="group-hover:bg-gradient-to-r group-hover:from-pink-500 group-hover:to-blue-500 group-hover:bg-clip-text group-hover:text-transparent">
              Add option
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default QuestionEditor;
